import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../services/api';
//import { toast } from 'react-hot-toast';
import toast from '../utils/toast';
import { DocumentArrowUpIcon, XMarkIcon, ArrowTopRightOnSquareIcon, DocumentIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import PasteUploadArea from './PasteUploadArea';
import { createVerificationRecord } from '../utils/documentVerification';

const DocumentUpload = ({ formSubmissionId, allProducts, onUploadComplete, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  const [uploadingRequirement, setUploadingRequirement] = useState(null);
  const [allUploadedDocuments, setAllUploadedDocuments] = useState({});

  // Get the currently selected product data
  const currentProduct = allProducts?.[selectedProductIndex];

  // Get available requirements for the current product
  const availableRequirements = currentProduct?.id?.documentRequirements || [];

  // Get documents for the current product
  const currentProductDocuments = allUploadedDocuments[`${currentProduct?.id?._id}-${currentProduct?.productIndex}`] || [];

  useEffect(() => {
    let mounted = true;

    const fetchDocuments = async () => {
      if (!formSubmissionId) return;

      try {
        setLoading(true);
        const response = await api.get(`/form-submissions/${formSubmissionId}/documents`);
        
        if (!mounted) return;

        // Group documents by product
        const groupedDocuments = response.data.reduce((acc, doc) => {
          const key = `${doc.productId}-${doc.productIndex}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(doc);
          return acc;
        }, {});

        setAllUploadedDocuments(groupedDocuments);
      } catch (error) {
        console.error('Error fetching documents:', error);
        if (mounted) {
          toast.error('Failed to fetch documents');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchDocuments();
    return () => {
      mounted = false;
    };
  }, [formSubmissionId]);

  const onDrop = async (acceptedFiles) => {
    if (!uploadingRequirement || !currentProduct) {
      toast.error('Please select a document type first');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      
      // Append the file
      formData.append('files', acceptedFiles[0]);
      
      // Append other data
      formData.append('productId', currentProduct.id._id);
      formData.append('productIndex', currentProduct.productIndex);
      formData.append('requirementName', uploadingRequirement);

      // Send the request
      const response = await api.post(`/form-submissions/${formSubmissionId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      // Create verification record for the uploaded document
      try {
        const uploadedDoc = response.data[0]; // Get the first uploaded document
        const verificationData = {
          documentType: 'formSubmissions',
          documentId: formSubmissionId,
          documentField: uploadingRequirement,
          fileUrl: uploadedDoc.document.url,
          fileName: acceptedFiles[0].name,
          fileSize: acceptedFiles[0].size
        };

        console.log(verificationData);

        const verificationResult = await createVerificationRecord(verificationData);
        
        if (verificationResult.verificationRequired) {
          toast.info('Document uploaded successfully. Verification required before approval.');
        } else {
          toast.success('Documents uploaded successfully');
        }
      } catch (verificationError) {
        console.error('Verification error:', verificationError);
        toast.success('Documents uploaded successfully');
      }

      // Update the documents list
      const key = `${currentProduct.id._id}-${currentProduct.productIndex}`;
      setAllUploadedDocuments(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), ...response.data]
      }));

      setUploadingRequirement(null);
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error(error.response?.data?.message || 'Failed to upload documents');
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: loading || !uploadingRequirement,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    }
  });

  const getProductDisplayName = (product, index) => {
    const fees = [
      `Govt: ${product['Government Fees']}`,
      `Prof: ${product['Professional Fees']}`
    ];
    const otherFees = product['Other Fees'];
    if (otherFees && otherFees.size > 0) {
      Array.from(otherFees.entries()).forEach(([key, value]) => {
        fees.push(`${key}: ${value}`);
      });
    }
    return `${product.id.name} #${index + 1} (${fees.join(', ')})`;
  };

  if (!isOpen) return null;
  if (!allProducts?.length) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="relative transform bg-white rounded-lg shadow-xl transition-all w-full max-w-3xl">
          <div className="flex flex-col">
            {/* Modal header - fixed */}
            <div className="flex-none px-4 py-4 border-b border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Upload Documents
                </h3>
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product
                </label>
                <select
                  value={selectedProductIndex}
                  onChange={(e) => {
                    setSelectedProductIndex(Number(e.target.value));
                    setUploadingRequirement(null);
                  }}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  {allProducts.map((product, index) => (
                    <option key={index} value={index}>
                      {getProductDisplayName(product, index)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Modal body - scrollable */}
            <div className="flex-1 px-4 pt-5 pb-4 sm:p-6">
              {loading && !allUploadedDocuments[`${currentProduct?.id?._id}-${currentProduct?.productIndex}`] ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <span className="ml-2">Loading...</span>
                </div>
              ) : (
                <>
                  {/* Document Requirements List */}
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                    <div className="px-4 py-5 sm:px-6">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Document Requirements</h3>
                    </div>
                    <div className="border-t border-gray-200">
                      <dl>
                        {availableRequirements.map((req, index) => {
                          const uploadedDoc = currentProductDocuments.find(doc => doc.requirementName === req.name);
                          const isUploading = uploadingRequirement === req.name;
                          
                          return (
                            <div key={req.name} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}>
                              <dt className="text-sm font-medium text-gray-500">{req.name}</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    {uploadedDoc ? (
                                      <>
                                        <DocumentArrowUpIcon className="h-5 w-5 text-green-500" />
                                        <a 
                                          href={uploadedDoc.document.url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 flex items-center"
                                        >
                                          {uploadedDoc.document.originalName}
                                          <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                                        </a>
                                      </>
                                    ) : (
                                      <span className="text-yellow-500">Not uploaded</span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setUploadingRequirement(isUploading ? null : req.name)}
                                    className={`px-3 py-1 text-sm rounded-md ${
                                      isUploading
                                        ? 'bg-gray-200 text-gray-600'
                                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                    }`}
                                  >
                                    {isUploading ? 'Cancel Upload' : 'Upload'}
                                  </button>
                                </div>

                                {/* Upload dropzone for this requirement */}
                                {isUploading && (
                                  <PasteUploadArea
                                    onFileSelect={(file) => onDrop([file])}
                                    accept={{
                                      'application/pdf': ['.pdf'],
                                      'application/msword': ['.doc'],
                                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                                      'image/jpeg': ['.jpg', '.jpeg'],
                                      'image/png': ['.png']
                                    }}
                                    maxSize={10 * 1024 * 1024}
                                    className="mt-4"
                                    dragDropText={loading ? 'Uploading...' : 'Drag & drop a file here, or click to select'}
                                    pasteText="or paste from clipboard (Ctrl+V)"
                                    disabled={loading}
                                  >
                                    <DocumentArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
                                    <p className="mt-1 text-sm text-gray-600">
                                      {loading ? 'Uploading...' : 'Drag & drop a file here, or click to select'}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-500">
                                      Supported formats: PDF, DOC, DOCX, JPG, PNG
                                    </p>
                                  </PasteUploadArea>
                                )}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex-none px-4 py-3 bg-gray-50 sm:px-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload; 